import { createHash } from "node:crypto";
import { existsSync, readdirSync } from "node:fs";
import { basename, join } from "node:path";

import {
  CLAUDE_ORGX_SKILL_PATH,
  CLAUDE_SKILLS_DIR,
  CURSOR_ORGX_RULE_PATH,
  ORGX_SKILL_EXTENSIONS_DIR,
  ORGX_WIZARD_STATE_PATH,
} from "../constants.js";
import { readTextIfExists, writeTextFile } from "./fs.js";
import type { OrgxPluginTarget } from "./plugins.js";
import { readWizardState, updateWizardState, type WizardSkillFileRecord } from "./wizard-state.js";

export const DEFAULT_ORGX_SKILL_PACKS = [
  "morning-briefing",
  "initiative-kickoff",
  "bulk-create",
  "nightly-recap",
  "orgx-design",
] as const;
export const PLUGIN_MANAGED_SKILL_TARGETS = ["claude", "codex"] as const;

// Directory entries in the useorgx/skills repo root that are not skill packs.
const EXCLUDED_PACK_DIRS = new Set([".github", "scripts"]);

// A directory in the skills repo is only a skill pack if it contains a SKILL.md.
// Dot-directories (.github, .codex, …) and tooling dirs (scripts) are repo
// infrastructure, not skills, and must never be materialized into a tool's
// skills folder.
function isSkillPackDir(name: string): boolean {
  return !name.startsWith(".") && !EXCLUDED_PACK_DIRS.has(name);
}

const ORGX_SKILLS_OWNER = "useorgx";
const ORGX_SKILLS_REPO = "skills";
const ORGX_SKILLS_REF = "main";
const SKILL_EXTENSION_SCOPES = ["user", "workspace", "project"] as const;
const COMPOSED_SKILL_MARKER = "ORGX SKILL COMPOSED v1";
const EXTENSION_FRONTMATTER_DELIMITER = "---";

export interface ManagedFileWrite {
  label: string;
  path: string;
  changed: boolean;
  reason?: string;
  skipped?: boolean;
  sourceUrl?: string;
}

export interface InstalledSkillPack {
  name: string;
  files: ManagedFileWrite[];
}

export interface SkillsInstallReport {
  writes: ManagedFileWrite[];
  packs: InstalledSkillPack[];
  notes: string[];
  extensions: SkillExtension[];
}

export interface InstallOrgxSkillsOptions {
  skillNames?: readonly string[];
  claudeSkillsDir?: string;
  claudeOrgxSkillPath?: string;
  cursorRulePath?: string;
  force?: boolean;
  fetchImpl?: typeof fetch;
  pluginTargets?: readonly string[];
  ref?: string;
  skillExtensionsDir?: string;
  statePath?: string;
}

export interface OrgxSkillsInstallPlan {
  installClaudeSkillBootstrap: boolean;
  installClaudeSkillPacks: boolean;
  notes: string[];
  pluginManagedTargets: OrgxPluginTarget[];
}

interface GithubDirectoryEntry {
  name: string;
  path: string;
  type: "file" | "dir";
  download_url: string | null;
}

interface RemoteSkillFile {
  path: string;
  sourceUrl: string;
}

export type SkillExtensionScope = "user" | "workspace" | "project";

export interface SkillExtension {
  content: string;
  enabled: boolean;
  id: string;
  path: string;
  scope: SkillExtensionScope;
  skillId: string;
  title: string;
}

export interface AddSkillExtensionOptions {
  content?: string;
  extensionsDir?: string;
  overwrite?: boolean;
  scope?: SkillExtensionScope;
  skillId: string;
  title?: string;
}

export interface SkillExtensionWrite {
  changed: boolean;
  created: boolean;
  extension: SkillExtension;
  path: string;
}

export interface ListSkillExtensionsOptions {
  extensionsDir?: string;
}

export interface SkillStatusReport {
  extensions: SkillExtension[];
  trackedFiles: WizardSkillFileRecord[];
}

export interface SkillStatusOptions {
  extensionsDir?: string;
  statePath?: string;
}

export const CURSOR_RULES_CONTENT = `# OrgX Rules

- Prefer \`workspace_id\` on OrgX tool calls. \`command_center_id\` is a deprecated alias and should only be used for backwards compatibility. If both are present, they must match.
- Treat \`orgx_act\` with \`force: true\` as an escape hatch. It bypasses hierarchy and proof checks, so only use it when the user explicitly wants that behavior and say why.
- Treat \`orgx_write continue_on_error\` as best-effort creation. Use it only when partial scaffolds are acceptable, and follow up on any failed children.
- Preserve \`_context\` when widget or app-rendering flows depend on client, conversation, session, or user metadata. Do not strip or rename it.
- Prefer \`orgx_search\` for pending approvals and state inspection before reaching for legacy aliases.
- Keep hosted OrgX MCP calls distinct from local OpenClaw/plugin surfaces before mutating config or entity state.
- Artifact proof must use durable sources: GitHub PR/commit/blob permalinks, published/public URLs, or absolute file paths / \`file://...\`.
- Never use OrgX wrapper pages like \`/live/...\`, \`/artifacts/...\`, or \`/console/...\` as proof links. \`preview_markdown\` is supplemental, not proof.
`;

const CLAUDE_ORGX_SKILL_CONTENT = `---
name: orgx
description: Use when working against the hosted OrgX MCP server for workspace-scoped planning, entity mutations, progress reporting, and decision handling.
version: 1.0.0
user-invocable: true
tags:
  - orchestration
  - productivity
  - workspace
---

# OrgX Hosted MCP

Use this skill when Claude is connected to the hosted OrgX MCP server at \`https://mcp.useorgx.com/mcp\`.

This skill is for the hosted OrgX tool surface, not the local OpenClaw plugin. Keep those surfaces distinct before mutating config or entity state.

## Operating Rules

- Prefer \`workspace_id\` on OrgX tools. \`command_center_id\` is a deprecated alias kept for compatibility.
- Treat \`orgx_act\` with \`force: true\` as a deliberate override only. It bypasses hierarchy and proof-chain checks.
- Treat \`orgx_write continue_on_error\` as best-effort scaffolding. Use it only when partial creation is acceptable and inspect failures afterward.
- Preserve \`_context\` when widget or app rendering depends on client, conversation, session, or user metadata.
- Prefer \`orgx_search\` over older aliases when you need pending decisions, blockers, or entity state.
- Artifact proof must use durable sources: GitHub PR/commit/blob permalinks, published/public URLs, or absolute file paths / \`file://...\`.
- Never use OrgX wrapper pages like \`/live/...\`, \`/artifacts/...\`, or \`/console/...\` as proof links. \`preview_markdown\` is supporting context only.

## Suggested Skill Packs

Install and use these packs alongside this base skill:

- \`morning-briefing\`
- \`initiative-kickoff\`
- \`bulk-create\`
- \`nightly-recap\`

## Suggested Workflow

1. Inspect state with \`mcp__orgx__orgx_search\`, \`mcp__orgx__get_initiative_pulse\`, or the relevant workspace-scoped read tool.
2. Mutate state with the narrowest tool that matches the task.
3. When you need a hard override, explain why before using \`force: true\`.
4. When you scaffold, decide up front whether \`continue_on_error\` is acceptable.
5. Carry \`_context\` through any widget-producing flows so the UI can render and resume correctly.
`;

function sha256(value: string): string {
  return createHash("sha256").update(value).digest("hex");
}

function normalizeSkillId(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9._-]*$/.test(normalized)) {
    throw new Error(
      "Skill id must start with a letter or number and only contain letters, numbers, '.', '_' or '-'.",
    );
  }
  return normalized;
}

function normalizeSkillExtensionScope(value: string | undefined): SkillExtensionScope {
  const normalized = (value ?? "user").trim().toLowerCase();
  if (!SKILL_EXTENSION_SCOPES.includes(normalized as SkillExtensionScope)) {
    throw new Error("Skill extension scope must be one of: user, workspace, project.");
  }
  return normalized as SkillExtensionScope;
}

function defaultExtensionTitle(skillId: string, scope: SkillExtensionScope): string {
  const prefix = scope === "user" ? "Personal" : scope === "workspace" ? "Workspace" : "Project";
  return `${prefix} ${skillId} behavior`;
}

function extensionFilePath(
  skillId: string,
  scope: SkillExtensionScope,
  extensionsDir = ORGX_SKILL_EXTENSIONS_DIR,
): string {
  return join(extensionsDir, `${scope}.${skillId}.md`);
}

function extensionTemplate(input: {
  skillId: string;
  scope: SkillExtensionScope;
  title: string;
  content?: string;
}): string {
  const body = input.content?.trim()
    ? input.content.trim()
    : [
        `# ${input.title}`,
        "",
        "- Add your custom behavior here.",
        "- These instructions are appended after the OrgX-managed core skill when the wizard syncs skills.",
      ].join("\n");

  return [
    EXTENSION_FRONTMATTER_DELIMITER,
    `skill: ${input.skillId}`,
    `scope: ${input.scope}`,
    "enabled: true",
    `title: ${input.title}`,
    EXTENSION_FRONTMATTER_DELIMITER,
    "",
    body,
    "",
  ].join("\n");
}

function parseFrontmatter(content: string): {
  body: string;
  data: Record<string, string>;
} {
  if (!content.startsWith(`${EXTENSION_FRONTMATTER_DELIMITER}\n`)) {
    return { body: content, data: {} };
  }

  const closeIndex = content.indexOf(`\n${EXTENSION_FRONTMATTER_DELIMITER}\n`, 4);
  if (closeIndex === -1) {
    return { body: content, data: {} };
  }

  const raw = content.slice(4, closeIndex);
  const body = content.slice(closeIndex + 5);
  const data: Record<string, string> = {};
  for (const line of raw.split("\n")) {
    const index = line.indexOf(":");
    if (index === -1) {
      continue;
    }
    const key = line.slice(0, index).trim();
    const value = line.slice(index + 1).trim();
    if (key) {
      data[key] = value;
    }
  }

  return { body, data };
}

function serializeFrontmatter(data: Record<string, string>, body: string): string {
  return [
    EXTENSION_FRONTMATTER_DELIMITER,
    ...Object.entries(data).map(([key, value]) => `${key}: ${value}`),
    EXTENSION_FRONTMATTER_DELIMITER,
    "",
    body.trimEnd(),
    "",
  ].join("\n");
}

function parseSkillExtension(path: string, content: string): SkillExtension | null {
  const fallbackId = basename(path, ".md");
  const fallbackParts = fallbackId.split(".");
  const fallbackScope = normalizeSkillExtensionScope(
    SKILL_EXTENSION_SCOPES.includes(fallbackParts[0] as SkillExtensionScope)
      ? fallbackParts.shift()
      : "user",
  );
  const fallbackSkillId = normalizeSkillId(fallbackParts.join(".") || fallbackId);
  const parsed = parseFrontmatter(content);
  const skillId = normalizeSkillId(parsed.data.skill || fallbackSkillId);
  const scope = normalizeSkillExtensionScope(parsed.data.scope || fallbackScope);
  const title = parsed.data.title?.trim() || defaultExtensionTitle(skillId, scope);

  return {
    content: parsed.body.trim(),
    enabled: parsed.data.enabled?.trim().toLowerCase() !== "false",
    id: `${scope}.${skillId}`,
    path,
    scope,
    skillId,
    title,
  };
}

export function listSkillExtensions(
  options: ListSkillExtensionsOptions = {},
): SkillExtension[] {
  const extensionsDir = options.extensionsDir ?? ORGX_SKILL_EXTENSIONS_DIR;
  if (!existsSync(extensionsDir)) {
    return [];
  }

  return readdirSync(extensionsDir, { withFileTypes: true })
    .filter((entry) => entry.isFile() && entry.name.endsWith(".md"))
    .map((entry) => {
      const path = join(extensionsDir, entry.name);
      const content = readTextIfExists(path);
      return content === null ? null : parseSkillExtension(path, content);
    })
    .filter((entry): entry is SkillExtension => Boolean(entry))
    .sort((left, right) => left.id.localeCompare(right.id));
}

export function addSkillExtension(
  options: AddSkillExtensionOptions,
): SkillExtensionWrite {
  const skillId = normalizeSkillId(options.skillId);
  const scope = normalizeSkillExtensionScope(options.scope);
  const path = extensionFilePath(skillId, scope, options.extensionsDir);
  const title = options.title?.trim() || defaultExtensionTitle(skillId, scope);
  const existing = readTextIfExists(path);

  if (existing !== null && options.overwrite !== true) {
    const extension = parseSkillExtension(path, existing);
    if (!extension) {
      throw new Error(`Could not parse existing skill extension at ${path}.`);
    }

    return {
      changed: false,
      created: false,
      extension,
      path,
    };
  }

  const nextContent = extensionTemplate({
    scope,
    skillId,
    title,
    ...(options.content !== undefined ? { content: options.content } : {}),
  });
  writeTextFile(path, nextContent);
  const extension = parseSkillExtension(path, nextContent);
  if (!extension) {
    throw new Error(`Could not parse newly written skill extension at ${path}.`);
  }

  return {
    changed: existing !== nextContent,
    created: existing === null,
    extension,
    path,
  };
}

export function setSkillExtensionEnabled(input: {
  enabled: boolean;
  extensionsDir?: string;
  scope?: SkillExtensionScope;
  skillId: string;
}): SkillExtensionWrite {
  const skillId = normalizeSkillId(input.skillId);
  const scope = normalizeSkillExtensionScope(input.scope);
  const path = extensionFilePath(skillId, scope, input.extensionsDir);
  const existing = readTextIfExists(path);
  if (existing === null) {
    if (!input.enabled) {
      throw new Error(`No ${scope} extension exists for ${skillId}.`);
    }

    return addSkillExtension({
      scope,
      skillId,
      ...(input.extensionsDir !== undefined ? { extensionsDir: input.extensionsDir } : {}),
    });
  }

  const parsed = parseFrontmatter(existing);
  const data = {
    skill: parsed.data.skill || skillId,
    scope: parsed.data.scope || scope,
    enabled: input.enabled ? "true" : "false",
    title: parsed.data.title || defaultExtensionTitle(skillId, scope),
  };
  const nextContent = serializeFrontmatter(data, parsed.body);
  if (nextContent !== existing) {
    writeTextFile(path, nextContent);
  }
  const extension = parseSkillExtension(path, nextContent);
  if (!extension) {
    throw new Error(`Could not parse skill extension at ${path}.`);
  }

  return {
    changed: nextContent !== existing,
    created: false,
    extension,
    path,
  };
}

function composeSkillContent(
  skillId: string,
  coreContent: string,
  extensions: readonly SkillExtension[],
): string {
  const enabled = extensions.filter((extension) => extension.enabled && extension.skillId === skillId);
  if (enabled.length === 0) {
    return coreContent;
  }

  const extensionBlocks = enabled.map((extension) => [
    `<!-- extension: ${extension.id} -->`,
    extension.content.trim(),
  ].join("\n"));

  return [
    `<!-- ${COMPOSED_SKILL_MARKER}`,
    `skill: ${skillId}`,
    `core-sha256: ${sha256(coreContent)}`,
    "generated: true",
    `edit-with: orgx-wizard skills extensions edit ${skillId}`,
    "-->",
    `<!-- ORGX CORE BEGIN ${skillId} -->`,
    coreContent.trimEnd(),
    `<!-- ORGX CORE END ${skillId} -->`,
    "",
    "<!-- ORGX USER EXTENSIONS BEGIN -->",
    ...extensionBlocks,
    "<!-- ORGX USER EXTENSIONS END -->",
    "",
  ].join("\n");
}

function encodeRepoPath(value: string): string {
  return value
    .split("/")
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function buildContentsUrl(path: string, ref: string): string {
  const encodedPath = encodeRepoPath(path);
  return `https://api.github.com/repos/${ORGX_SKILLS_OWNER}/${ORGX_SKILLS_REPO}/contents/${encodedPath}?ref=${encodeURIComponent(ref)}`;
}

function resolveGitHubError(status: number, path: string): string {
  if (status === 404) {
    return `OrgX skill pack '${path}' was not found in ${ORGX_SKILLS_OWNER}/${ORGX_SKILLS_REPO}.`;
  }

  return `GitHub returned HTTP ${status} while loading '${path}' from ${ORGX_SKILLS_OWNER}/${ORGX_SKILLS_REPO}.`;
}

async function readResponseText(response: Response): Promise<string> {
  return response.text();
}

async function fetchDirectoryEntries(
  path: string,
  fetchImpl: typeof fetch,
  ref: string,
): Promise<GithubDirectoryEntry[]> {
  const response = await fetchImpl(buildContentsUrl(path, ref), {
    headers: {
      Accept: "application/vnd.github+json",
    },
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    throw new Error(resolveGitHubError(response.status, path));
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error(`Expected '${path}' to be a directory in ${ORGX_SKILLS_OWNER}/${ORGX_SKILLS_REPO}.`);
  }

  return data
    .filter((entry): entry is GithubDirectoryEntry => {
      if (!entry || typeof entry !== "object") return false;
      return entry.type === "file" || entry.type === "dir";
    })
    .sort((left, right) => left.path.localeCompare(right.path));
}

async function listRemoteSkillFiles(
  path: string,
  fetchImpl: typeof fetch,
  ref: string,
): Promise<RemoteSkillFile[]> {
  const entries = await fetchDirectoryEntries(path, fetchImpl, ref);
  const files: RemoteSkillFile[] = [];

  for (const entry of entries) {
    if (entry.type === "dir") {
      files.push(...await listRemoteSkillFiles(entry.path, fetchImpl, ref));
      continue;
    }

    if (!entry.download_url) {
      throw new Error(`GitHub did not provide a download URL for '${entry.path}'.`);
    }

    files.push({
      path: entry.path,
      sourceUrl: entry.download_url,
    });
  }

  return files;
}

async function fetchRemoteText(sourceUrl: string, fetchImpl: typeof fetch): Promise<string> {
  const response = await fetchImpl(sourceUrl, {
    headers: {
      Accept: "text/plain",
    },
    signal: AbortSignal.timeout(10_000),
  });

  if (!response.ok) {
    throw new Error(`GitHub returned HTTP ${response.status} while downloading ${sourceUrl}.`);
  }

  return readResponseText(response);
}

function getTrackedSkillFile(
  records: readonly WizardSkillFileRecord[],
  path: string,
): WizardSkillFileRecord | undefined {
  return records.find((record) => record.path === path);
}

function writeManagedFile(
  path: string,
  content: string,
  label: string,
  sourceUrl?: string,
  tracking?: {
    coreContent?: string;
    force?: boolean;
    records: readonly WizardSkillFileRecord[];
    skillId: string;
    stagedRecords: Map<string, WizardSkillFileRecord>;
  },
): ManagedFileWrite {
  const existing = readTextIfExists(path);
  const tracked = tracking ? getTrackedSkillFile(tracking.records, path) : undefined;
  if (
    tracking &&
    existing !== null &&
    tracked &&
    sha256(existing) !== tracked.contentSha256 &&
    tracking.force !== true
  ) {
    return {
      label,
      path,
      changed: false,
      skipped: true,
      reason: "manual edits detected; move them into a skill extension or rerun with --force",
      ...(sourceUrl ? { sourceUrl } : {}),
    };
  }

  const changed = existing !== content;

  if (changed) {
    writeTextFile(path, content);
  }

  if (tracking) {
    tracking.stagedRecords.set(path, {
      contentSha256: sha256(content),
      ...(tracking.coreContent ? { coreSha256: sha256(tracking.coreContent) } : {}),
      path,
      skillId: tracking.skillId,
      updatedAt: new Date().toISOString(),
    });
  }

  return {
    label,
    path,
    changed,
    ...(sourceUrl ? { sourceUrl } : {}),
  };
}

export function resolveSkillPackNames(
  requested: readonly string[] = [],
): string[] {
  if (requested.length === 0 || requested.includes("all")) {
    return [...DEFAULT_ORGX_SKILL_PACKS];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const name of requested) {
    const next = name.trim();
    if (!next || seen.has(next)) {
      continue;
    }
    seen.add(next);
    normalized.push(next);
  }

  return normalized;
}

export function resolvePluginManagedSkillTargets(
  requested: readonly string[] = [],
): OrgxPluginTarget[] {
  const seen = new Set<OrgxPluginTarget>();
  const targets: OrgxPluginTarget[] = [];

  for (const rawTarget of requested) {
    const target = rawTarget.trim().toLowerCase() as OrgxPluginTarget;
    if (!PLUGIN_MANAGED_SKILL_TARGETS.includes(target as typeof PLUGIN_MANAGED_SKILL_TARGETS[number])) {
      continue;
    }
    if (seen.has(target)) {
      continue;
    }

    seen.add(target);
    targets.push(target);
  }

  return targets;
}

export function planOrgxSkillsInstall(
  pluginTargets: readonly string[] = [],
): OrgxSkillsInstallPlan {
  const pluginManagedTargets = resolvePluginManagedSkillTargets(pluginTargets);
  const managesClaudeSkills = pluginManagedTargets.includes("claude");
  const notes: string[] = [];

  if (managesClaudeSkills) {
    notes.push(
      "Claude Code plugin is installed, so the wizard skipped overlapping standalone Claude OrgX skill files and Claude skill-pack sync.",
    );
  }
  if (pluginManagedTargets.includes("codex")) {
    notes.push(
      "Codex companion plugin bundles its own OrgX skills, so `wizard skills add` only manages standalone editor assets outside Codex.",
    );
  }

  return {
    installClaudeSkillBootstrap: !managesClaudeSkills,
    installClaudeSkillPacks: !managesClaudeSkills,
    notes,
    pluginManagedTargets,
  };
}

async function fetchAvailablePackNames(
  fetchImpl: typeof fetch,
  ref: string,
): Promise<string[]> {
  const entries = await fetchDirectoryEntries("", fetchImpl, ref);
  return entries
    .filter((e) => e.type === "dir" && isSkillPackDir(e.name))
    .map((e) => e.name);
}

async function installSkillPack(
  skillName: string,
  claudeSkillsDir: string,
  fetchImpl: typeof fetch,
  ref: string,
  tracking: {
    extensions: readonly SkillExtension[];
    force?: boolean;
    records: readonly WizardSkillFileRecord[];
    stagedRecords: Map<string, WizardSkillFileRecord>;
  },
): Promise<InstalledSkillPack> {
  const rootPath = skillName;
  const files = await listRemoteSkillFiles(rootPath, fetchImpl, ref);
  const writes: ManagedFileWrite[] = [];

  for (const file of files) {
    const coreContent = await fetchRemoteText(file.sourceUrl, fetchImpl);
    const relativePath = file.path.slice(`${rootPath}/`.length);
    const content = relativePath === "SKILL.md"
      ? composeSkillContent(skillName, coreContent, tracking.extensions)
      : coreContent;
    writes.push(
      writeManagedFile(
        join(claudeSkillsDir, skillName, relativePath),
        content,
        `${skillName}/${relativePath}`,
        file.sourceUrl,
        {
          coreContent,
          records: tracking.records,
          skillId: skillName,
          stagedRecords: tracking.stagedRecords,
          ...(tracking.force !== undefined ? { force: tracking.force } : {}),
        },
      ),
    );
  }

  return {
    name: skillName,
    files: writes,
  };
}

export async function installOrgxSkills(
  options: InstallOrgxSkillsOptions = {},
): Promise<SkillsInstallReport> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (!fetchImpl) {
    throw new Error("Global fetch is unavailable in this runtime.");
  }

  const ref = options.ref ?? ORGX_SKILLS_REF;
  const claudeSkillsDir = options.claudeSkillsDir ?? CLAUDE_SKILLS_DIR;
  const claudeOrgxSkillPath = options.claudeOrgxSkillPath ?? CLAUDE_ORGX_SKILL_PATH;
  const cursorRulePath = options.cursorRulePath ?? CURSOR_ORGX_RULE_PATH;
  const statePath = options.statePath ?? ORGX_WIZARD_STATE_PATH;
  const extensions = listSkillExtensions(
    options.skillExtensionsDir !== undefined ? { extensionsDir: options.skillExtensionsDir } : {},
  );
  const enabledExtensions = extensions.filter((extension) => extension.enabled);
  const trackedRecords = readWizardState(statePath)?.skillFiles?.files ?? [];
  const stagedRecords = new Map<string, WizardSkillFileRecord>();
  const plan = planOrgxSkillsInstall(options.pluginTargets ?? []);
  const requestedNames = options.skillNames ?? [];
  let skillNames: string[];
  if (requestedNames.length === 0 || requestedNames.includes("all")) {
    try {
      skillNames = await fetchAvailablePackNames(fetchImpl, ref);
    } catch {
      skillNames = [...DEFAULT_ORGX_SKILL_PACKS];
    }
  } else {
    skillNames = resolveSkillPackNames(requestedNames);
  }

  const writes: ManagedFileWrite[] = [
    writeManagedFile(
      cursorRulePath,
      composeSkillContent("cursor-rules", CURSOR_RULES_CONTENT, extensions),
      "cursor-rules",
      undefined,
      {
        coreContent: CURSOR_RULES_CONTENT,
        records: trackedRecords,
        skillId: "cursor-rules",
        stagedRecords,
        ...(options.force !== undefined ? { force: options.force } : {}),
      },
    ),
  ];
  if (plan.installClaudeSkillBootstrap) {
    writes.push(
      writeManagedFile(
        claudeOrgxSkillPath,
        composeSkillContent("orgx", CLAUDE_ORGX_SKILL_CONTENT, extensions),
        "claude-orgx-skill",
        undefined,
        {
          coreContent: CLAUDE_ORGX_SKILL_CONTENT,
          records: trackedRecords,
          skillId: "orgx",
          stagedRecords,
          ...(options.force !== undefined ? { force: options.force } : {}),
        },
      ),
    );
  }

  const packs: InstalledSkillPack[] = [];
  if (plan.installClaudeSkillPacks) {
    for (const skillName of skillNames) {
      packs.push(await installSkillPack(skillName, claudeSkillsDir, fetchImpl, ref, {
        extensions,
        records: trackedRecords,
        stagedRecords,
        ...(options.force !== undefined ? { force: options.force } : {}),
      }));
    }
  }

  if (stagedRecords.size > 0) {
    updateWizardState((current) => {
      const previousFiles = current.skillFiles?.files ?? [];
      const nextFiles = new Map<string, WizardSkillFileRecord>();
      for (const record of previousFiles) {
        nextFiles.set(record.path, record);
      }
      for (const record of stagedRecords.values()) {
        nextFiles.set(record.path, record);
      }

      return {
        ...current,
        skillFiles: {
          files: [...nextFiles.values()].sort((left, right) => left.path.localeCompare(right.path)),
          updatedAt: new Date().toISOString(),
        },
      };
    }, statePath);
  }

  const notes = [...plan.notes];
  if (enabledExtensions.length > 0) {
    notes.push(
      `Applied ${enabledExtensions.length} enabled OrgX skill extension${enabledExtensions.length === 1 ? "" : "s"} while syncing skills/rules.`,
    );
  }
  if (
    writes.some((write) => write.skipped) ||
    packs.some((pack) => pack.files.some((file) => file.skipped))
  ) {
    notes.push("Some generated skill files were skipped because local manual edits were detected.");
  }

  return {
    extensions,
    notes,
    writes,
    packs,
  };
}

export function getSkillStatus(
  options: SkillStatusOptions = {},
): SkillStatusReport {
  const state = readWizardState(options.statePath ?? ORGX_WIZARD_STATE_PATH);
  return {
    extensions: listSkillExtensions(
      options.extensionsDir !== undefined ? { extensionsDir: options.extensionsDir } : {},
    ),
    trackedFiles: state?.skillFiles?.files ?? [],
  };
}
